import React from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { t } from 'i18next';
import { Checkbox, DriftSelect, TextField, Typography } from 'drift-ui';
import { formatNumberString } from '../utils/utils';
import { states } from '../utils/constants';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import creditCardIcon from '../../../assets/images/credit-card.svg';
import styles from '../styles/CreditCardForm.scss';
import commonStyles from '../styles/common.scss';

export interface CreditCardFormData {
  cardNumber?: string;
  expirationDate?: string;
  securityCode?: string;
  firstName?: string;
  lastName?: string;
  billingAddress?: string;
  billingState?: string;
  zipCode?: string;
  agreementOfPurchase?: boolean;
}

interface CreditCardFormProps {
  control: Control<CreditCardFormData>;
  errors: FieldErrors<CreditCardFormData>;
  cardNumber?: string;
  expirationDate?: string;
}

const isAmexCard = (cardNumber?: string) => {
  if (!cardNumber) {
    return false;
  }
  return cardNumber.startsWith('34') || cardNumber.startsWith('37');
};

const CreditCardForm: React.FC<CreditCardFormProps> = ({ control, errors, cardNumber, expirationDate }) => {
  const stateOptions = states.map((state) => ({
    label: state.name,
    value: state.label,
  }));

  const onChangeCardNumber =
    (rhfOnChange: ControllerRenderProps['onChange']) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const input = event.target.value;
      const isAmex = isAmexCard(input);

      rhfOnChange({
        target: {
          value: formatNumberString({
            format: isAmex ? '0000-000000-00000' : '0000-0000-0000-0000',
            previousValue: cardNumber,
            value: input,
          }).slice(0, isAmex ? 17 : 19),
        },
      });
    };

  const onChangeExpDate =
    (rhfOnChange: ControllerRenderProps['onChange']) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const text = event.target.value;
      rhfOnChange({
        target: {
          value: formatNumberString({
            format: '00/00',
            previousValue: expirationDate,
            value: text,
          }).slice(0, 5),
        },
      });
    };

  return (
    <>
      <div className={commonStyles.subHeader}>{t('Card Information')}</div>
      <div className={commonStyles.row}>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              label={t('First Name')}
              placeholder={t('First Name')}
              type="text"
              helperText={errors?.firstName?.message}
              error={!!errors?.firstName}
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              label={t('Last Name')}
              placeholder={t('Last Name')}
              type="text"
              helperText={errors?.lastName?.message}
              error={!!errors?.lastName}
            />
          )}
        />
      </div>
      <div className={commonStyles.row}>
        <Controller
          name="cardNumber"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                required
                label={t('Card Number')}
                placeholder={t('Card Number')}
                type="text"
                onChange={onChangeCardNumber(field.onChange)}
                helperText={errors?.cardNumber?.message}
                error={!!errors?.cardNumber}
              />
              <img src={creditCardIcon} className={styles.img} alt="Credit Card" />
            </>
          )}
        />
      </div>
      <div className={commonStyles.row}>
        <Controller
          name="expirationDate"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              className={commonStyles.leftRowItem}
              label={t('Expiration Date')}
              placeholder={t('Expiration Date')}
              type="text"
              onChange={onChangeExpDate(field.onChange)}
              helperText={errors?.expirationDate?.message}
              error={!!errors?.expirationDate}
            />
          )}
        />
        <Controller
          name="securityCode"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              label={t('Security Code')}
              placeholder={t('Security Code')}
              type="text"
              helperText={errors?.securityCode?.message}
              error={!!errors?.securityCode}
            />
          )}
        />
      </div>
      <div className={commonStyles.subHeader}>{t('Billing Address')}</div>
      <div className={commonStyles.row}>
        <Controller
          name="billingAddress"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              label={t('Street Address')}
              placeholder={t('Street Address')}
              type="text"
              helperText={errors?.billingAddress?.message}
              error={!!errors?.billingAddress}
            />
          )}
        />
      </div>
      <div className={commonStyles.row}>
        <Controller
          name="billingState"
          control={control}
          render={({ field }) => (
            <DriftSelect
              {...field}
              label={t('State')}
              options={stateOptions}
              size="medium"
              helperText={errors?.billingState?.message}
              error={!!errors?.billingState}
            />
          )}
        />
        <Controller
          name="zipCode"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              className={styles.rightRowItem}
              label={t('Zip Code')}
              placeholder={t('Zip Code')}
              type="text"
              helperText={errors?.zipCode?.message}
              error={!!errors?.zipCode}
            />
          )}
        />
      </div>
      <div className={commonStyles.row}>
        <Controller name="agreementOfPurchase" control={control} render={({ field }) => <Checkbox {...field} />} />
        <div className={commonStyles.certifyText}>
          {t('I certify I possess the authority to purchase on behalf of the company.')}
        </div>
      </div>
      <Typography color="error">{errors?.agreementOfPurchase?.message}</Typography>
    </>
  );
};

export default CreditCardForm;
